import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import deleteUser from "api/requests/deleteUser";
import { SnackBarMessages, useSnackBar } from "context/SnackBarContext";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useToast } from "hooks/useToast";
import { ToastType } from "redux/reducers/toasts";

import Button from "components/botanic/Button";
import { InformationalModal } from "components/botanic/Modal";

const DeleteAccountButton = () => {
  const [showAccountDeleteModal, setShowAccountDeleteModal] = useState(false);
  const [hasDeleteAccountError, setHasDeleteAccountError] = useState(false);
  const deleteAccountButtonRef = useRef(null);
  const router = useRouter();
  const { addSnackBarItem } = useSnackBar();
  const { notify, dismiss } = useToast();
  const [activeToastsIds, setActiveToastsIds] = useState<string[]>([]);
  const domainCountryCode = useDomainCountryCode();

  useEffect(() => {
    // account deletion error messaging
    if (hasDeleteAccountError) {
      const contents = (
        <div>
          Unable to load.{" "}
          <button
            className="underline"
            onClick={(e) => {
              e.preventDefault();
              setShowAccountDeleteModal(true);
              setHasDeleteAccountError(false);
            }}
          >
            Try again.
          </button>
        </div>
      );
      const toastId = notify(contents, ToastType.Error);
      setActiveToastsIds((prev) => [...prev, toastId]);
    } else {
      activeToastsIds.map((id) => dismiss(id));
      setActiveToastsIds([]);
    }
  }, [hasDeleteAccountError]);

  const handleDeleteAccountConfirmation = async () => {
    setHasDeleteAccountError(false);
    setShowAccountDeleteModal(false);
    const successfullyDeleted = await deleteUser(domainCountryCode);
    if (successfullyDeleted) {
      await router.push("/");
      addSnackBarItem(SnackBarMessages.accountDeleted);
    } else {
      setHasDeleteAccountError(true);
    }
  };

  return (
    <>
      <div className="row justify-center mb-lg border-t border-light-grey">
        <button
          ref={deleteAccountButtonRef}
          className="col mt-xl text-error text-[14px] font-bold underline"
          onClick={(e) => {
            e.preventDefault();
            setShowAccountDeleteModal(true);
          }}
        >
          delete account
        </button>
      </div>
      <div className="row justify-center mb-section">
        <p className="text-center max-w-[343px] text-xs text-grey italic">
          Deleting your account will delete data associated with you from
          Leafly’s servers. For more details, visit our{" "}
          <Link href="/info/terms-of-use">terms &amp; conditions</Link>.
        </p>
      </div>
      {showAccountDeleteModal && (
        <InformationalModal
          returnFocusRef={deleteAccountButtonRef}
          onDismissed={() => setShowAccountDeleteModal(false)}
          aria-labelledby="delete-confirmation"
          mobileFullscreen={false}
          expository={true}
        >
          <h3 className="text-center px-lg pt-lg" id="delete-confirmation">
            Are you sure you want to delete your account?
          </h3>
          <p className="text-center px-md pt-md">
            This will delete information currently linked to your account. You
            will not be able to reactivate your account once deleted, but can
            re-use your email address to create a new one.
          </p>
          <Button
            className="mt-xl bg-error mx-auto"
            onClick={handleDeleteAccountConfirmation}
            data-testid="delete-account-confirm"
          >
            delete account
          </Button>
        </InformationalModal>
      )}
    </>
  );
};

export default DeleteAccountButton;
